import Controller from "controllers/application_controller"

/**
 * Manages a stack of event listeners for a given target, ensuring that
 * they are called in reverse order of their addition. This is useful
 * for managing event listeners that need to be called in a specific order,
 * or when stopImmediatePropagation is needed to prevent earlier instances
 * from being called. In practice, this is useful for binding global keybinds
 * from multiple elements (eg. modal dialogs when there can be multiple on the page).
 */
class EventListenerStack {
  listeners = {}

  handler = (event) => {
    const stack = this.listeners[event.type]
    if (stack && stack.length > 0) {
      const original = event.stopImmediatePropagation
      let immediatePropagationStopped = false
      event.stopImmediatePropagation = function() {
        immediatePropagationStopped = true
        original.call(this)
      }

      for (let i = stack.length - 1; i >= 0; i--) {
        stack[i](event)
        if (immediatePropagationStopped) {
          break
        }
      }
      event.stopImmediatePropagation = original
    }
  }

  constructor(target) {
    this.target = target
  }

  addEventListener(event, callback) {
    let stack = this.listeners[event] ??= []
    stack.push(callback)
    if (stack.length === 1) {
      this.target.addEventListener(event, this.handler)
    }
  }

  removeEventListener(event, callback) {
    let stack = this.listeners[event] ?? []
    const index = stack.indexOf(callback)
    if (index !== -1) {
      stack.splice(index, 1)

      if (stack.length === 0) {
        this.target.removeEventListener(event, this.handler)
      }
    }
  }
}

const escapeListeners = new EventListenerStack(document)

/**
 * Controls an individual <dialog> modal element.
 */
export default class extends Controller {
  static identifier = "modal"
  static targets = ['form']

  backdropPointerDown = false

  connect() {
    this.addEventListener('click', event => this.cancelFromBackdrop(event))
    this.addEventListener('cancel', event => this.cancel(event))
    this.addEventListener('pointerdown', event => this.trackBackdropPointerDown(event))
    this.addEventListener(escapeListeners, 'keydown', event => {
      // A component inside the modal (a [popover] listbox, a menu) can claim
      // Escape for its own dismissal by cancelling the keydown. Closing the
      // dialog on top of that would discard the whole form. Native close
      // requests already skip a cancelled keydown; this listener sits outside
      // that machinery, so it has to check for itself.
      if (this.element.open && event.key === 'Escape' && !event.defaultPrevented) {
        event.stopImmediatePropagation()
        this.cancel(event)
      }
    })
  }

  showModal() {
    this.element.show()
  }

  close(event) {
    event?.preventDefault()

    requestAnimationFrame(() => this.element.close())
  }

  closeOnSuccess(event) {
    if (event.detail.success) {
      this.close(event)
    }
  }

  cancel(event) {
    event?.preventDefault()

    if (this.hasFormTarget) {
      let willCancel = this.dispatch('modal:will-cancel', {
        target: this.formTarget,
        detail: { element: this.element, modal: this, event }
      })

      if (willCancel.defaultPrevented) {
        return
      }
    }

    this.close(event)
  }

  cancelFromBackdrop(event) {
    // Close if click was directly on the dialog (outside wrapper)
    const pointerDownOnBackdrop = this.backdropPointerDown
    this.backdropPointerDown = false

    // detail >= 1 indicates a pointer-generated click
    if (event.detail >= 1 && pointerDownOnBackdrop && event.target === this.element) {
      this.cancel(event)
    }
  }

  trackBackdropPointerDown(event) {
    this.backdropPointerDown = event.target === this.element
  }

  // We aren't using backdrop at the moment, the <dialog> itself is the backdrop.
  // /**
  //  * It is not possible to determine if the click is on the <dialog> backdrop,
  //  * but the click is still fired to the dialog element directly as the target.
  //  * This method checks if the click is outside the dialog element.
  //  */
  // isDirectClickOutside(event) {
  //   // Only proceed if this is a mouse event
  //   if (event instanceof MouseEvent && event.target === this.element) {
  //     let rect = this.element.getBoundingClientRect();
  //     if (rect.left > event.clientX ||
  //         rect.right < event.clientX ||
  //         rect.top > event.clientY ||
  //         rect.bottom < event.clientY
  //     ) {
  //       return true
  //     }

  //     return false
  //   }
  // }
}
